
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {CURSOR_MARKER_COLOR} from "../CursorPointMarker.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {createFeature} from "../Feature.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {FILL_COLOR, FILL_OPACITY} from "./areaTool.js";
import {fitRectangleFromThreePoints, buildRectangleCorners} from "../geometry/rectangleFromThreePoints.js";
import {createDialogOverlay, fillDialog, pvpButton} from "../DialogHelpers.js";
import {toVector3Array, toPlainPoint} from "./shapeCaptureHelpers.js";
import {createMarker, createDashedLine, setLinePositions} from "./perpendicularCapture.js";
import {createStyledLabel} from "../DigitizationStyle.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {createLivePlaneEstimator} from "./livePlaneEstimator.js";
import {projectOntoPlane} from "../geometry/localPlaneFit.js";
import {distanceBetween} from "../geometry/measurementGeometry.js";
import {formatLength} from "./distanceTool.js";
import {formatArea, averagePosition} from "./areaTool.js";

import {t, T} from "../i18n/index.js";
const PREVIEW_COLOR = 0xffdc00; // matches every other construction-preview in this codebase
const WORLD_UP = {x: 0, y: 0, z: 1};

// "Rektangel". Capture is click P1 -> click P2 (fixes the base direction +
// initial length - task: "P1/basis skal være stabil") -> dynamic width/side
// (mouse position relative to the base line, in the currently estimated
// plane) -> click to confirm. The surrounding point cloud is queried
// continuously (throttled, via tools/livePlaneEstimator.js) to estimate the
// rectangle's own local 3D plane while the width is being dragged - the
// operator never places a manual 3rd reference point, the plane comes from
// the visible surface instead. The point cloud only ORIENTS the rectangle:
// P1/P2 are re-projected onto the estimated plane (geometry/localPlaneFit.js's
// projectOntoPlane - moves each point along the plane's normal only, so the
// base direction/length the operator clicked stays the stable reference,
// never independently re-derived from the point cloud) and the exact
// rectangle (4 corners, 90° neighbour angles, parallel opposite sides -
// see geometry/rectangleFromThreePoints.js's own doc comment for why this
// always holds by construction) is regenerated from those three
// plane-projected points via the SAME fitRectangleFromThreePoints()/
// buildRectangleCorners() this tool always used - the point cloud never
// touches the 4 corners individually. The underlying registered feature is
// an ordinary Polygon (task: "Den underliggende GIS-feature skal fortsat
// være almindelig polygongeometri") - only `feature.domainData.rectangle`
// remembers the parametric definition (see geometry/parametricShapeStore.js).
export const RectangleTool = {
	id: "digitize_rectangle",
	name: T("Rektangel"),
	category: "basic",
	kind: "registration",
	toolbarGroup: "registration",
	toolbarOrder: 50,
	icon: "digitize_rectangle.svg",
	resultGeometryType: "Polygon",
	tooltip: T("Digitalisér et rektangel: klik hjørne, klik sideretning, flyt musen for bredde/side - punktskyen estimerer automatisk det lokale plan."),
	instructions: {
		1: T("Punkt 1/2: Venstreklik for rektanglets første hjørne."),
		2: T("Punkt 2/2: Venstreklik for at angive sideretning og længde."),
		width: T("Flyt musen for at angive bredde og side - punktskyen estimerer automatisk det lokale plan. Venstreklik for at afslutte."),
	},
	fillColor: FILL_COLOR,
	fillOpacity: FILL_OPACITY,
	customCapture(dm, tool){ runRectangleCapture(dm, tool); },
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the finished rectangle keeps its result label.
	decorateLoaded(visual){ enableRectangleResultLabel(visual); },
};

DigitizationTools.register(RectangleTool);

// The finished rectangle's result label (name, length, width, area): a pure function of the 4 corners (in buildRectangleCorners()'s
// perimeter-walk order), shared by the capture flow and the restore hook.
export function rectangleSummaryLines(raw, v){
	if(raw.length < 4) return null;
	const length = distanceBetween(raw[0], raw[1]);
	const width = distanceBetween(raw[1], raw[2]);
	return [
		v.name,
		t("Længde: {0}", formatLength(length)),
		t("Bredde: {0}", formatLength(width)),
		t("Areal: {0}", formatArea(length * width)),
	];
}

export function enableRectangleResultLabel(visual){
	visual.enableSummaryLabel(rectangleSummaryLines, (raw) => averagePosition(raw));
}

function buildRectangleShape(anchor, u, v, lengthM, widthM){
	return {
		type: "rectangle",
		anchor: [anchor.x, anchor.y, anchor.z],
		u: [u.x, u.y, u.z],
		v: [v.x, v.y, v.z],
		lengthM,
		widthM,
	};
}

function planeFromShape(shape){
	return {
		anchor: {x: shape.anchor[0], y: shape.anchor[1], z: shape.anchor[2]},
		u: {x: shape.u[0], y: shape.u[1], z: shape.u[2]},
		v: {x: shape.v[0], y: shape.v[1], z: shape.v[2]},
	};
}

// Live-preview dialog shared by "Rektangel" (brand-new) and "Rediger
// rektangel…" (existing) - same architecture as circleTool.js's
// openCircleDialog()/VectorEditingMenu.js's openArcDialog(). `anchor`/`u`/`v`
// are the STABLE reference (task DEL C: "Første hjørne og basisretningen fra
// første til andet inputpunkt skal være den stabile konstruktion-reference"
// - adopted as-is; no reason found in the existing editing lifecycle to
// prefer a different anchor) and never change here - only lengthM/widthM
// do. "Vend side" (task) flips widthM's sign, moving the rectangle to the
// opposite side of the base line without touching anchor/u/v.
//
// Displayed/entered in CENTIMETER (task: "Alle brugerindtastede
// dimensionsværdier skal være i centimeter") - PointView's own internal
// geometry (lengthM/widthM) stays in METER throughout; the ×100/÷100
// conversion happens only at this UI boundary, right where the value is
// read from or written to the DOM.
function openRectangleDialog(dm, visual, {anchor, u, v}, initialLengthM, initialWidthM, {isNewFeature} = {}){
	return new Promise((resolve) => {
		const feature = dm.getFeature(visual);
		if(!feature){ resolve(false); return; }

		const originalPoints = visual.points.map((p) => ({x: p.position.x, y: p.position.y, z: p.position.z}));
		const originalShape = feature.domainData.rectangle ? {...feature.domainData.rectangle} : null;

		let lengthM = initialLengthM;
		let widthM = initialWidthM;
		let done = false;

		const applyPreview = () => {
			const corners = buildRectangleCorners(anchor, u, v, lengthM, widthM);
			visual.setPoints(toVector3Array(corners));
			dm.featureManager.setDomainData(feature.id, "rectangle", buildRectangleShape(anchor, u, v, lengthM, widthM));
		};

		const {overlay, box} = createDialogOverlay(dm, 280);
		fillDialog(box, {
			title: `${isNewFeature ? t("Rektangel") : t("Rediger rektangel")}`,
			description: t(`Ændringen vises direkte i 3D, mens du skriver. Hjørne og sideretning bevares.`),
			body: `<label class="pvp-label">${t("Længde [cm]")}</label>
				<input class="pvp-input" type="number" step="any" min="0" value="${(lengthM * 100).toFixed(1)}" data-role="length"
				 />
				<label class="pvp-label">${t("Bredde [cm]")}</label>
				<input class="pvp-input" type="number" step="any" min="0" value="${(Math.abs(widthM) * 100).toFixed(1)}" data-role="width"
				 />`,
			actions: pvpButton('flip', t("Vend side"), 'start') + pvpButton('cancel', t("Annuller")) + pvpButton('commit', t("Godkend"), 'primary'),
		});

		const lengthInput = box.querySelector('[data-role="length"]');
		const widthInput = box.querySelector('[data-role="width"]');
		lengthInput.focus();
		lengthInput.select();

		applyPreview();

		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};

		const finish = (committed) => {
			if(done) return;
			done = true;
			cleanup();
			if(committed){
				dm.clearMultiSelection();
			}else{
				visual.setPoints(toVector3Array(originalPoints));
				if(originalShape) dm.featureManager.setDomainData(feature.id, "rectangle", originalShape);
				else dm.featureManager.clearDomainData(feature.id, "rectangle");
			}
			resolve(committed);
		};

		lengthInput.addEventListener('input', () => {
			const parsedCm = parseFloat(lengthInput.value);
			if(isFinite(parsedCm) && parsedCm > 0){ lengthM = parsedCm / 100; applyPreview(); }
		});
		widthInput.addEventListener('input', () => {
			const parsedCm = parseFloat(widthInput.value);
			if(isFinite(parsedCm) && parsedCm > 0){ widthM = Math.sign(widthM || 1) * (parsedCm / 100); applyPreview(); }
		});
		box.querySelector('[data-action="flip"]').addEventListener('click', () => {
			widthM = -widthM;
			applyPreview();
		});
		box.querySelector('[data-action="commit"]').addEventListener('click', () => finish(true));
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(false));
		const onCancelEvent = () => finish(false);
		box.addEventListener('keydown', (e) => {
			if(e.key === 'Escape'){ e.stopPropagation(); finish(false); }
			if(e.key === 'Enter'){ e.preventDefault(); finish(true); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}

// Phase 1: P1, P2 - the stable base corner + direction/length. Hand-rolled
// (not pipeTool.js's plain captureFixedPoints - see the bug this replaced:
// captureFixedPoints only ever reacts to clicks, so it has no mousemove hook
// to drive a live P1->cursor preview, and its own per-click callback never
// applies computeScreenSpaceScale() to the placed marker, leaving it at
// createMarker()'s raw WORLD-SPACE radius-1 geometry - a ~2m solid
// octahedron that reads as "an enormous yellow shape" at ordinary zoom
// levels once it's the only thing on screen between P1 and P2). This mirrors
// every other construction-preview tool's own established pattern instead
// (parallelOffsetTool.js/perpendicularCapture.js/circleTool.js's own radius
// phase): a `viewer` "update" listener continuously rescales every visible
// marker via computeScreenSpaceScale(), and a `mousemove` handler drives the
// live cursor marker + dashed line to it - task: "linje fra P1 til cursor"
// + "normal markør ved cursorens aktuelle position".
function runRectangleCapture(dm, tool){
	const viewer = dm.viewer;
	const domElement = viewer.renderer.domElement;

	const p1Marker = createMarker(CURSOR_MARKER_COLOR);
	// markøren ved cursoren er den fælles blå kugle (DigitizationModule.cursorMarker), ikke en egen pyramide her
	const baseLine = createDashedLine(PREVIEW_COLOR);
	dm.scene.add(p1Marker, baseLine);

	let p1 = null;
	let finished = false;

	const updateScales = () => {
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const w = size.width, h = size.height;
		if(p1Marker.visible){
			const s = computeScreenSpaceScale(camera, p1Marker.getWorldPosition(new THREE.Vector3()), camera.position, w, h, ScreenSpaceSizes.computedPoint);
			p1Marker.scale.set(s, s, s);
		}
		baseLine.material.resolution.set(w, h);
	};
	const onUpdate = () => updateScales();
	viewer.addEventListener("update", onUpdate);

	const cleanup = () => {
		domElement.removeEventListener("mousemove", onMouseMove, false);
		domElement.removeEventListener("mouseup", onMouseUp, false);
		viewer.removeEventListener("update", onUpdate);
		viewer.removeEventListener("cancel_insertions", onCancel);
	};

	const removeConstructionGeometry = () => {
		dm.scene.remove(p1Marker);
		dm.scene.remove(baseLine);
	};

	const onMouseMove = (e) => {
		if(finished || dm.isPaused()) return;
		const picked = dm._pickPlacementPosition(e);
		if(!picked){ baseLine.visible = false; return; }

		if(p1){
			setLinePositions(baseLine, p1Marker.position, picked);
			baseLine.visible = true;
		}
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		removeConstructionGeometry();
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;
		if(e.button !== THREE.MOUSE.LEFT) return;

		const location = dm._pickPlacementPosition(e);
		if(!location) return;

		if(!p1){
			p1 = location.clone();
			dm._markOperationStarted();
			p1Marker.position.copy(p1);
			p1Marker.visible = true;
			dm._showInstruction(tool.name, tool.instructions[2]);
			return;
		}

		const p2 = location.clone();
		finished = true;
		cleanup();
		removeConstructionGeometry();
		runRectangleWidthPhase(dm, tool, p1, p2);
	};

	dm._showInstruction(tool.name, tool.instructions[1]);
	domElement.addEventListener("mousemove", onMouseMove, false);
	domElement.addEventListener("mouseup", onMouseUp, false);
	viewer.addEventListener("cancel_insertions", onCancel);
}

// Phase 2: dynamic width + side (mouse position relative to the P1->P2
// base line, measured IN the currently estimated plane) + continuous
// point-cloud plane estimation (tools/livePlaneEstimator.js) - a third
// click commits. P1/P2 are re-projected onto the plane on every redraw
// (never stored as "corrected" separately - geometry/localPlaneFit.js's
// projectOntoPlane), so the operator's own base direction/length stays the
// authoritative reference; only its out-of-plane component is ever
// adjusted, and only by the plane the point cloud actually supports.
function runRectangleWidthPhase(dm, tool, p1Vec, p2Vec){
	const viewer = dm.viewer;
	const domElement = viewer.renderer.domElement;
	const p1Raw = toPlainPoint(p1Vec);
	const p2Raw = toPlainPoint(p2Vec);
	const baseLengthRaw = p1Vec.distanceTo(p2Vec);
	const roiCenter = p1Vec.clone().add(p2Vec).multiplyScalar(0.5);

	dm._showInstruction(tool.name, tool.instructions.width);

	const visual = new FeatureVisual("Polygon");
	visual.setInfo(tool.name, "");
	if(tool.fillColor) visual.enableFill(tool.fillColor, tool.fillOpacity);
	dm.scene.add(visual);

	// User-requested result label (task: "Tilføj en resultat label for...
	// Rektangel-værktøjet"), registered up front so it live-updates through
	// the whole dynamic drag AND the post-capture dialog alike (task §7:
	// "Result labels... skal fortsat opdateres fra den aktuelle preview-
	// geometri"). Derived purely from `raw` (the 4 corners, in
	// buildRectangleCorners()'s own c0->c1->c2->c3 perimeter-walk order) -
	// see FeatureVisual.js's own enableSummaryLabel()/_sync() doc comments
	// for why one registration is enough. The average of all 4 corners of a
	// rectangle is exactly its centre (opposite corners are symmetric about
	// it), same reasoning areaTool.js's own averagePosition() anchor
	// already relies on.
	enableRectangleResultLabel(visual);

	const readout = createStyledLabel("");
	readout.visible = false;
	dm.scene.add(readout);

	const estimator = createLivePlaneEstimator(dm);

	let lastFit = null; // {anchor,u,v,lengthM,widthM}, plane-projected - see fitRectangleFromThreePoints
	let lastPlane = estimator.currentPlane(p1Raw, WORLD_UP);
	let lastPickedRaw = null;
	let finished = false;

	const updateScales = () => {
		if(!readout.visible) return;
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const s = computeScreenSpaceScale(camera, readout.getWorldPosition(new THREE.Vector3()), camera.position, size.width, size.height, ScreenSpaceSizes.label);
		readout.scale.set(s, s, s);
	};
	const onUpdate = () => updateScales();
	viewer.addEventListener("update", onUpdate);

	const redraw = (plane) => {
		if(!lastPickedRaw) return;
		const finalP1 = projectOntoPlane(p1Raw, plane);
		const finalP2 = projectOntoPlane(p2Raw, plane);
		const pickedProjected = projectOntoPlane(toPlainPoint(lastPickedRaw), plane);

		const fit = fitRectangleFromThreePoints(finalP1, finalP2, pickedProjected);
		if(!fit) return; // degenerate (mouse on the base line) - keep the last valid preview

		const corners = buildRectangleCorners(fit.anchor, fit.u, fit.v, fit.lengthM, fit.widthM);
		visual.setPoints(toVector3Array(corners));

		readout.visible = true;
		readout.setText(t("Længde: {0}  Bredde: {1}", formatLength(fit.lengthM), formatLength(Math.abs(fit.widthM))));
		const mid = averagePosition(corners.map((c) => [c.x, c.y, c.z]));
		readout.position.set(mid.x, mid.y, mid.z);

		lastFit = fit;
		lastPlane = plane;
	};

	const cleanup = () => {
		domElement.removeEventListener("mousemove", onMouseMove, false);
		domElement.removeEventListener("mouseup", onMouseUp, false);
		viewer.removeEventListener("update", onUpdate);
		viewer.removeEventListener("cancel_insertions", onCancel);
	};

	const onMouseMove = (e) => {
		if(finished || dm.isPaused()) return;
		const picked = dm._pickPlacementPosition(e);
		if(!picked) return;
		lastPickedRaw = picked;

		const plane = estimator.currentPlane(p1Raw, WORLD_UP);
		redraw(plane);

		const radialExtent = Math.max(baseLengthRaw, lastFit ? Math.abs(lastFit.widthM) : 0, 0.1) / 2 + 0.3;
		estimator.poke(roiCenter, radialExtent, p1Raw, WORLD_UP, (fit) => {
			if(finished) return;
			redraw(fit);
		});
	};

	const finalize = () => {
		finished = true;
		cleanup();
		dm.scene.remove(readout);
		estimator.dispose();

		const {anchor, u, v, lengthM, widthM} = lastFit;

		const feature = createFeature({
			geometryType: "Polygon",
			coordinates: visual.toCoordinates(),
			category: tool.category,
			tool: tool.id,
			featureType: tool.name,
			name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		});
		visual.setInfo(feature.properties.name, feature.properties.description);
		dm.registerNewFeature(feature, visual);
		dm.featureManager.setDomainData(feature.id, "rectangle", buildRectangleShape(anchor, u, v, lengthM, widthM));

		openRectangleDialog(dm, visual, {anchor, u, v}, lengthM, widthM, {isNewFeature: true}).then((committed) => {
			if(!committed) dm.removeFeature(feature.id); // task: "Cancel efterlader ingen permanent feature"
			dm.stopInsertion();
		});
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;
		if(e.button !== THREE.MOUSE.LEFT) return;
		if(!lastFit) return; // no valid preview yet - nothing to commit
		finalize();
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		dm.scene.remove(visual);
		dm.scene.remove(readout);
		estimator.dispose();
	};

	domElement.addEventListener("mousemove", onMouseMove, false);
	domElement.addEventListener("mouseup", onMouseUp, false);
	viewer.addEventListener("cancel_insertions", onCancel);
}

// "Rediger rektangel…" (task DEL C) - bound from VectorEditingMenu.js's
// whole-object menu when the selected Polygon carries
// feature.domainData.rectangle.
export async function runEditRectangle(dm, visual){
	const feature = dm.getFeature(visual);
	if(!feature || !feature.domainData.rectangle) return;
	const shape = feature.domainData.rectangle;
	await openRectangleDialog(dm, visual, planeFromShape(shape), shape.lengthM, shape.widthM, {isNewFeature: false});
}

// "Opløs rektangel" (task DEL C) - removes only the parametric definition;
// the current coordinates are left exactly as they are.
export function runDissolveRectangle(dm, visual){
	const feature = dm.getFeature(visual);
	if(!feature) return;
	dm.featureManager.clearDomainData(feature.id, "rectangle");
}
